/**
 * Canonical Super Admin URL paths and legacy page ↔ URL mapping.
 * URL is the source of truth for the current page (and Phase 2 subroutes).
 */

import { resolveTabFromSegment, MODULE_TAB_CONFIG } from './module-tabs.js';
import { buildAuditoriaEventPath, buildKbNewArticlePath } from './entity-routes.js';

/** @type {Record<string, string>} */
export const PAGE_TO_PATH = {
  dashboard: '/dashboard',
  lojas: '/lojas',
  'nova-loja': '/lojas/nova',
  planos: '/planos',
  'planos-permissoes': '/planos/permissoes',
  inadimplencia: '/inadimplencia',
  billing: '/billing',
  equipe: '/equipe',
  usuarios: '/equipe',
  perfis: '/equipe/perfis',
  config: '/configuracoes',
  auditoria: '/auditoria',
  integrations: '/integracoes',
  observabilidade: '/observabilidade',
  repairs: '/repairs',
  tickets: '/suporte/tickets',
  'knowledge-base': '/suporte/base-conhecimento',
  'support-reports': '/suporte/relatorios',
  'notificacoes-ops': '/notificacoes',
  'tech-spec': '/sistema/especificacao-tecnica',
  atendimento: '/dashboard',
};

/** Static pathname → legacy page (exact roots only). */
export const PATH_TO_PAGE = {
  '/dashboard': 'dashboard',
  '/lojas': 'lojas',
  '/lojas/nova': 'nova-loja',
  '/planos': 'planos',
  '/planos/permissoes': 'planos-permissoes',
  '/inadimplencia': 'inadimplencia',
  '/billing': 'billing',
  '/equipe': 'equipe',
  '/equipe/perfis': 'perfis',
  '/configuracoes': 'config',
  '/auditoria': 'auditoria',
  '/integracoes': 'integrations',
  '/observabilidade': 'observabilidade',
  '/repairs': 'repairs',
  '/suporte/tickets': 'tickets',
  '/suporte/base-conhecimento': 'knowledge-base',
  '/suporte/relatorios': 'support-reports',
  '/notificacoes': 'notificacoes-ops',
  '/sistema/especificacao-tecnica': 'tech-spec',
  '/login': 'login',
};

/** Legacy path aliases → canonical path (replace). */
export const LEGACY_PATH_REDIRECTS = {
  '/usuarios': '/equipe',
  '/planos-permissoes': '/planos/permissoes',
  '/config': '/configuracoes',
  '/integrations': '/integracoes',
  '/knowledge-base': '/suporte/base-conhecimento',
  '/support-reports': '/suporte/relatorios',
  '/notificacoes-ops': '/notificacoes',
  '/atendimento': '/dashboard',
  '/admin/support/tickets': '/suporte/tickets',
  '/admin/support/knowledge-base': '/suporte/base-conhecimento',
  '/admin/support/reports': '/suporte/relatorios',
  '/admin/billing': '/billing',
  '/admin/observability': '/observabilidade',
};

/**
 * Prefix matchers for nested module paths → legacy page.
 * Longer prefixes first.
 */
const PREFIX_PAGE_RULES = [
  { prefix: '/suporte/base-conhecimento', page: 'knowledge-base' },
  { prefix: '/suporte/relatorios', page: 'support-reports' },
  { prefix: '/suporte/tickets', page: 'tickets' },
  { prefix: '/planos/permissoes', page: 'planos-permissoes' },
  { prefix: '/inadimplencia', page: 'inadimplencia' },
  { prefix: '/integracoes', page: 'integrations' },
  { prefix: '/observabilidade', page: 'observabilidade' },
  { prefix: '/notificacoes', page: 'notificacoes-ops' },
  { prefix: '/auditoria', page: 'auditoria' },
  { prefix: '/billing', page: 'billing' },
  { prefix: '/repairs', page: 'repairs' },
  { prefix: '/equipe', page: 'equipe' },
];

/**
 * Resolve tenant identifier for URL path segments.
 * Prefers store.id (incl. stor_*) so URLs align with tezus-route.storeId persistence.
 */
export function resolveTenantId(storeRef) {
  if (storeRef == null || storeRef === '') return null;
  if (typeof storeRef === 'string' || typeof storeRef === 'number') {
    const raw = String(storeRef).trim();
    return raw || null;
  }
  if (typeof storeRef !== 'object') return null;
  if (storeRef.id != null && String(storeRef.id).trim() !== '') {
    return String(storeRef.id);
  }
  if (storeRef.tenant_id != null && String(storeRef.tenant_id).trim() !== '') {
    return String(storeRef.tenant_id);
  }
  return null;
}

/**
 * Resolve tenant id for API calls — mirrors App.jsx pre-router behavior.
 */
export function resolveTenantIdForApi(storeRef) {
  if (!storeRef) return null;
  if (typeof storeRef === 'string' || typeof storeRef === 'number') {
    return String(storeRef);
  }
  if (storeRef.tenant_id != null) return storeRef.tenant_id;
  const m = String(storeRef.id || '').match(/(\d+)$/);
  return m ? m[1] : storeRef.id;
}

/**
 * Match a store from a URL tenant param against list entries.
 */
export function matchStoreByTenantParam(stores, tenantParam) {
  if (tenantParam == null || tenantParam === '') return null;
  const list = Array.isArray(stores) ? stores : [];
  const key = String(tenantParam);
  return list.find((s) => {
    if (!s) return false;
    if (String(s.id) === key) return true;
    if (s.tenant_id != null && String(s.tenant_id) === key) return true;
    const resolved = resolveTenantId(s);
    return resolved != null && String(resolved) === key;
  }) || null;
}

export function normalizePathname(pathname) {
  if (pathname == null || pathname === '') return '/';
  let p = String(pathname);
  const q = p.indexOf('?');
  const h = p.indexOf('#');
  let cut = p.length;
  if (q >= 0) cut = Math.min(cut, q);
  if (h >= 0) cut = Math.min(cut, h);
  p = p.slice(0, cut);
  try {
    p = decodeURIComponent(p);
  } catch (e) {
    /* keep raw */
  }
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

/**
 * Convert a pathname to a legacy page id (+ optional tenantId / entity ids).
 * @returns {{ page: string, tenantId?: string, eventId?: string, tabSegment?: string } | null}
 */
export function urlToLegacyPage(pathname) {
  const p = normalizePathname(pathname);
  if (PATH_TO_PAGE[p]) {
    return { page: PATH_TO_PAGE[p] };
  }

  const cockpitTabMatch = p.match(/^\/lojas\/([^/]+)\/cockpit(?:\/([^/]+))?$/);
  if (cockpitTabMatch) {
    return {
      page: 'cockpit',
      tenantId: cockpitTabMatch[1],
      tabSegment: cockpitTabMatch[2] || null,
    };
  }
  const lojaMatch = p.match(/^\/lojas\/([^/]+)$/);
  if (lojaMatch && lojaMatch[1] !== 'nova') {
    return { page: 'loja', tenantId: lojaMatch[1] };
  }

  // Auditoria event detail
  const auditMatch = p.match(/^\/auditoria\/([^/]+)$/);
  if (auditMatch) {
    return { page: 'auditoria', eventId: auditMatch[1] };
  }

  // KB article new (still knowledge-base page)
  if (p === '/suporte/base-conhecimento/artigos/novo'
    || /^\/suporte\/base-conhecimento\/artigos\/[^/]+\/editar$/.test(p)) {
    return { page: 'knowledge-base' };
  }

  // Tickets with id segment (structural; may be blocked by MVP guard)
  const ticketMatch = p.match(/^\/suporte\/tickets\/([^/]+)$/);
  if (ticketMatch) {
    const seg = ticketMatch[1];
    const known = resolveTabFromSegment('tickets', seg);
    if (!known.invalid && MODULE_TAB_CONFIG.tickets.tabs.some((t) => t.segment === seg)) {
      return { page: 'tickets', tabSegment: seg };
    }
    // Could be ticket id — still tickets page for guard purposes
    return { page: 'tickets', ticketId: seg };
  }

  for (const rule of PREFIX_PAGE_RULES) {
    if (p === rule.prefix || p.startsWith(`${rule.prefix}/`)) {
      const rest = p.slice(rule.prefix.length).replace(/^\//, '');
      return {
        page: rule.page,
        tabSegment: rest && !rest.includes('/') ? rest : (rest || null),
      };
    }
  }

  if (LEGACY_PATH_REDIRECTS[p]) {
    return urlToLegacyPage(LEGACY_PATH_REDIRECTS[p]);
  }

  // Pattern legacy: /admin/support/tickets/:id
  const adminTicket = p.match(/^\/admin\/support\/tickets\/([^/]+)$/);
  if (adminTicket) {
    return { page: 'tickets', ticketId: adminTicket[1] };
  }
  const adminArticle = p.match(/^\/admin\/support\/knowledge-base\/articles\/([^/]+)$/);
  if (adminArticle) {
    return { page: 'knowledge-base' };
  }

  return null;
}

export function sidebarActiveId(page) {
  if (page === 'loja' || page === 'cockpit' || page === 'nova-loja') return 'lojas';
  if (page === 'usuarios') return 'equipe';
  return page;
}

export { buildAuditoriaEventPath, buildKbNewArticlePath };
